import React, { useState, useEffect } from "react";
import {
  Upload,
  User,
  Mail,
  Phone,
  LogOut,
  Eye,
  EyeOff,
  Lock,
  Clock,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import userService from "../../services/userServices";
import { useSiteContext } from "../../context/SiteContext";
import { TIMEZONES, getCurrentTimezone } from "../../utils/timezones";
import { LANGUAGES } from "../../utils/languages";
const Profile = () => {
  const { setUser } = useSiteContext();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [meetingReminders, setMeetingReminders] = useState(false);
  const [securityAlerts, setSecurityAlerts] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // E.164-compatible: optional leading "+", 7–15 digits after stripping
  // formatting characters (spaces, hyphens, parens, dots). Empty is allowed
  // because the phone field is optional on this profile.
  const validatePhone = (raw) => {
    const value = (raw || "").trim();
    if (!value) return "";
    if (!/^\+?[0-9\s().-]+$/.test(value)) {
      return "Phone number contains invalid characters";
    }
    const digits = value.replace(/\D/g, "");
    if (digits.length < 7) {
      return "Phone number is too short (minimum 7 digits)";
    }
    if (digits.length > 15) {
      return "Phone number is too long (maximum 15 digits)";
    }
    return "";
  };

  // Password change states
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);

  // Timezone and preferences states
  const [preferences, setPreferences] = useState({
    timezone: getCurrentTimezone(),
    language: "en",
  });

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.log("No token found, redirecting to login");
      navigate("/login");
    } else {
      getProfile();
    }
  }, [navigate]);

  // Load user preferences after profile is loaded
  useEffect(() => {
    if (profileData && !isLoading) {
      // Try to load preferences from a separate endpoint if available
      loadUserPreferences();
    }
  }, [profileData, isLoading]);

  const getProfile = async () => {
    try {
      const res = await userService.getUserProfile();
      setProfileData(res.user);
      setUser(res.user);
      localStorage.setItem("user", JSON.stringify(res.user));

      // Set preferences from user profile data
      if (res.user) {
        setPreferences((prev) => ({
          ...prev,
          timezone: res.user.timezone || getCurrentTimezone(),
          language: res.user.language || "en",
        }));

        // Set meeting reminders and security alerts from user data
        if (res.user.meetingReminders !== undefined) {
          setMeetingReminders(res.user.meetingReminders);
        }
        if (res.user.securityAlerts !== undefined) {
          setSecurityAlerts(res.user.securityAlerts);
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (field === "phone") {
      setPhoneError(validatePhone(value));
    }
  };

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/)) strength++;
    if (password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    return strength;
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "newPassword") {
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePreferenceChange = (field, value) => {
    setPreferences((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const loadUserPreferences = async () => {
    try {
      const res = await userService.getUserPreferences();
      if (res.preferences) {
        setPreferences((prev) => ({
          ...prev,
          timezone: res.preferences.timezone || getCurrentTimezone(),
          language: res.preferences.language || "en",
        }));

        if (res.preferences.meetingReminders !== undefined) {
          setMeetingReminders(res.preferences.meetingReminders);
        }
        if (res.preferences.securityAlerts !== undefined) {
          setSecurityAlerts(res.preferences.securityAlerts);
        }
      }
    } catch (error) {
      console.log("Could not load user preferences, using defaults");
    }
  };

  const handlePreferencesSave = async () => {
    try {
      await userService.updateUserPreferences({
        timezone: preferences.timezone,
        language: preferences.language,
        meetingReminders,
        securityAlerts,
      });
      toast.success("Preferences updated successfully");
    } catch (error) {
      console.log("error", error);
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("Something went wrong, please try again later");
      }
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      uploadImage(file);
    }
  };

  const handleSave = async () => {
    console.log("Saving profile:", profileData);
    const phoneValidationError = validatePhone(profileData.phone);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      toast.error(phoneValidationError);
      return;
    }
    // Implement save functionality
    try {
      await userService.updateUserProfile({
        name: profileData.name,
        username: profileData.username,
        phone: profileData.phone,
      });

      getProfile();
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(`Error updating profile: ${error.message}`);
    }
  };

  const handlePasswordSave = async () => {
    // Validate password fields
    if (!passwordData.currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!passwordData.newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }

    setIsPasswordLoading(true);
    try {
      await userService.changePassword({
        oldPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      // Clear password fields
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      setPasswordStrength(0);

      toast.success("Password changed successfully");
    } catch (error) {
      console.log("error", error);
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(`Something went wrong, please try again later`);
      }
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const uploadImage = (file) => {
    if (file.size > 3 * 1024 * 1024) {
      toast.error("File size exceeds 5 MB limit.");
      return;
    }
    // form data
    const formData = new FormData();
    formData.append("image", file);
    setIsUploadingImage(true);
    userService
      .uploadProfilePicture(formData)
      .then((res) => {
        toast.success("Profile Picture updated");
        getProfile();
      })
      .catch((error) => {
        toast.error(error);
      })
      .finally(() => {
        setIsUploadingImage(false);
      });
  };

  const getInitials = () => {
    const name = profileData.name;
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("meetingRole");
    localStorage.removeItem("meetingId");
    localStorage.removeItem("meetingToken");
    localStorage.removeItem("meetingMicOn");
    localStorage.removeItem("meetingWebcamOn");
    window.location.href = "/login";
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen text-white p-6 flex items-center justify-center"
        style={{ backgroundColor: "#565167" }}
      >
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white p-4 md:p-6">
      <div className="flex flex-col items-center justify-center gap-5">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-6 uppercase tracking-widest text-center">
          Profile Settings
        </h1>
        <div className="relative">
          {profileData && profileData?.logo ? (
            <img
              src={profileData?.logo}
              alt="Profile1"
              className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover"
              onError={(e) => {
                console.log("Image failed to load:", e);
                e.target.style.display = "none";
              }}
            />
          ) : (
            <span className="text-xl md:text-2xl font-bold text-white border-2 border-gray-300 rounded-full w-24 h-24 md:w-32 md:h-32 flex items-center justify-center">
              {getInitials()}
            </span>
          )}
          {isUploadingImage && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/60">
              <Loader2 className="w-8 h-8 md:w-10 md:h-10 text-white animate-spin" />
            </div>
          )}
        </div>

        <div className="relative flex justify-center">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={isUploadingImage}
            className={`absolute inset-0 w-full h-full opacity-0 ${
              isUploadingImage ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          />
          <div
            className={`w-56 md:w-64 h-14 md:h-16 border-2 border-dashed rounded-lg flex items-center justify-center gap-2 transition-colors ${
              isUploadingImage
                ? "border-gray-500 bg-[#3f3b4d] cursor-not-allowed"
                : "border-gray-400 bg-[#565167] cursor-pointer hover:border-gray-300"
            }`}
          >
            {isUploadingImage ? (
              <>
                <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
                <span className="text-gray-300 text-xl font-bold">
                  UPLOADING...
                </span>
              </>
            ) : (
              <span className="text-gray-400 text-xl font-bold">
                CLICK OR DROP HERE
              </span>
            )}
          </div>
        </div>
        <p className="text-gray-400 text-xs md:text-sm mb-4 text-center px-4">
          Recommended: Square image (400x400 px) for best quality. Max file
          size: 3 MB. JPG or PNG.
        </p>
      </div>

      <div className="bg-[#000000]/20 p-4 md:p-10 rounded-2xl flex flex-col items-center justify-center gap-6 md:gap-10">
        <h1 className="uppercase  text-xl font-bold text-white tracking-wide text-center">
          personal information
        </h1>
        <div className="flex flex-col md:flex-row w-full items-center justify-center gap-4 md:gap-8">
          <div className="space-y-2 w-full md:w-1/2">
            <label className="text-gray-300 text-sm font-medium">
              Display Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Name"
                value={profileData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          {/* Username Field */}
          <div className="space-y-2 w-full md:w-1/2">
            <label className="text-gray-300 text-sm font-medium">
              Username
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Username"
                value={profileData.username}
                onChange={(e) => handleInputChange("username", e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>
        </div>

        {/* Email Field */}
        <div className="flex flex-col md:flex-row w-full items-center justify-center gap-4 md:gap-8">
          <div className="space-y-2 w-full md:w-1/2">
            <label className="text-gray-300 text-sm font-medium">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={profileData.email}
                disabled
                className="w-full pl-10 pr-4 py-3 bg-gray-600 border border-gray-500 rounded-lg text-gray-300 cursor-not-allowed"
              />
            </div>
          </div>

          {/* Phone Field */}
          <div className="space-y-2 w-full md:w-1/2">
            <label className="text-gray-300 text-sm font-medium">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="e.g. +1 555 123 4567"
                value={profileData.phone || ""}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                onBlur={(e) => setPhoneError(validatePhone(e.target.value))}
                aria-invalid={!!phoneError}
                className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 ${
                  phoneError
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-green-500 focus:border-green-500"
                }`}
              />
            </div>
            {phoneError && (
              <p className="text-red-400 text-xs mt-1">{phoneError}</p>
            )}
          </div>
        </div>

        <div className="flex w-full items-center justify-center">
          <button
            onClick={handleSave}
            disabled={!!phoneError}
            className="px-8 py-3 border-[#1CD760] border-2 text-[#1CD760] font-medium rounded-lg transition-colors duration-200 hover:bg-[#1CD760] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#1CD760]"
          >
            Save Changes
          </button>
        </div>
      </div>

      <div className="bg-[#000000]/20 p-4 md:p-10 rounded-2xl flex flex-col items-center justify-center gap-6 md:gap-10 mt-6 md:mt-10">
        <h1 className="uppercase text-lg md:text-xl font-bold text-white tracking-wide text-center">
          Security
        </h1>
        <div className="flex flex-col md:flex-row w-full items-center justify-center gap-4 md:gap-8">
          <div className="space-y-2 w-full md:w-1/2">
            <label className="text-gray-300 text-sm font-medium">
              Current Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type={showPasswords.current ? "text" : "password"}
                placeholder="Enter current password"
                value={passwordData.currentPassword}
                onChange={(e) =>
                  handlePasswordChange("currentPassword", e.target.value)
                }
                className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility("current")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.current ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2 w-full md:w-1/2"></div>
        </div>

        <div className="flex flex-col md:flex-row w-full items-center justify-center gap-4 md:gap-8">
          <div className="space-y-2 w-full md:w-1/2">
            <label className="text-gray-300 text-sm font-medium">
              New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type={showPasswords.new ? "text" : "password"}
                placeholder="Enter new password"
                value={passwordData.newPassword}
                onChange={(e) =>
                  handlePasswordChange("newPassword", e.target.value)
                }
                className="w-full pl-10 pr-12 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility("new")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.new ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {/* Fixed height container for password strength indicator */}
            <div className="h-8 flex flex-col justify-center">
              <div className="w-full flex items-center justify-center gap-2">
                <span
                  className={`w-1/3 rounded-2xl h-1 ${
                    passwordStrength >= 1
                      ? passwordStrength >= 3
                        ? "bg-green-500"
                        : "bg-yellow-500"
                      : "bg-[#818181]"
                  }`}
                ></span>
                <span
                  className={`w-1/3 rounded-2xl h-1 ${
                    passwordStrength >= 2
                      ? passwordStrength >= 3
                        ? "bg-green-500"
                        : "bg-yellow-500"
                      : "bg-[#818181]"
                  }`}
                ></span>
                <span
                  className={`w-1/3 rounded-2xl h-1 ${
                    passwordStrength >= 3 ? "bg-green-500" : "bg-[#818181]"
                  }`}
                ></span>
              </div>
              <p className="text-gray-400 text-sm text-center">
                Password strength:{" "}
                {passwordStrength === 0
                  ? "None"
                  : passwordStrength <= 2
                  ? "Weak"
                  : passwordStrength <= 3
                  ? "Medium"
                  : "Strong"}
              </p>
            </div>
          </div>

          <div className="space-y-2 w-full md:w-1/2">
            <label className="text-gray-300 text-sm font-medium">
              Confirm New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type={showPasswords.confirm ? "text" : "password"}
                placeholder="Confirm new password"
                value={passwordData.confirmNewPassword}
                onChange={(e) =>
                  handlePasswordChange("confirmNewPassword", e.target.value)
                }
                className={`w-full pl-10 pr-12 py-3 bg-white border rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  passwordData.confirmNewPassword &&
                  passwordData.newPassword !== passwordData.confirmNewPassword
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility("confirm")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.confirm ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {/* Fixed height container for error message to match the strength indicator */}
            <div className="h-8 flex flex-col justify-center">
              {passwordData.confirmNewPassword &&
              passwordData.newPassword !== passwordData.confirmNewPassword ? (
                <p className="text-red-400 text-sm text-center">
                  Passwords do not match
                </p>
              ) : (
                <p className="text-gray-400 text-sm text-center">
                  Confirm your new password
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-center">
          <button
            onClick={handlePasswordSave}
            className="px-8 py-3 border-[#1CD760] border-2 text-[#1CD760] font-medium rounded-lg transition-colors duration-200 hover:bg-[#1CD760] hover:text-white"
          >
            Save Changes
          </button>
        </div>
      </div>

      <div className="bg-[#000000]/20 p-4 md:p-10 rounded-2xl flex flex-col items-center justify-center gap-6 md:gap-10 mt-6 md:mt-10">
        <h1 className="uppercase text-lg md:text-xl font-bold text-white tracking-wide text-center">
          Preferences
        </h1>
        <div className="flex flex-col md:flex-row w-full items-center justify-center gap-4 md:gap-8">
          <div className="space-y-2 w-full md:w-1/2">
            <label className="text-white text-sm font-medium">Language</label>
            <div className="relative">
              <select
                value={preferences.language}
                onChange={(e) =>
                  handlePreferenceChange("language", e.target.value)
                }
                className="w-full pl-10 bg-[#000000]/20 pr-4 py-3 border border-gray-300 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name} ({lang.nativeName})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2 w-full md:w-1/2">
            <label className="text-white text-sm font-medium">Time Zone</label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={preferences.timezone}
                onChange={(e) =>
                  handlePreferenceChange("timezone", e.target.value)
                }
                className="w-full pl-10 pr-4 py-3 bg-[#000000]/20 border border-gray-300 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row w-full items-center justify-center gap-4 md:gap-8">
          <div className="space-y-2 w-full md:w-1/2 flex items-center justify-between">
            <h1 className="text-white text-sm font-medium">
              Meeting Reminders
            </h1>
            <div
              className="flex items-center gap-3 cursor-pointer select-none"
              onClick={() => setMeetingReminders(!meetingReminders)}
            >
              <span
                className={`text-xs font-semibold tracking-wide w-7 text-left transition-colors duration-200 ${
                  meetingReminders ? "text-[#00c853]" : "text-gray-300"
                }`}
              >
                {meetingReminders ? "ON" : "OFF"}
              </span>
              <div
                role="switch"
                aria-checked={meetingReminders}
                className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${
                  meetingReminders ? "bg-[#00c853]" : "bg-gray-600"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-200 ${
                    meetingReminders ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2 w-full md:w-1/2 flex items-center justify-between">
            <h1 className="text-white text-sm font-medium">Security Alerts</h1>
            <div
              className="flex items-center gap-3 cursor-pointer select-none"
              onClick={() => setSecurityAlerts(!securityAlerts)}
            >
              <span
                className={`text-xs font-semibold tracking-wide w-7 text-left transition-colors duration-200 ${
                  securityAlerts ? "text-[#00c853]" : "text-gray-300"
                }`}
              >
                {securityAlerts ? "ON" : "OFF"}
              </span>
              <div
                role="switch"
                aria-checked={securityAlerts}
                className={`relative w-12 h-7 rounded-full transition-colors duration-200 ${
                  securityAlerts ? "bg-[#00c853]" : "bg-gray-600"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-200 ${
                    securityAlerts ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-center">
          <button
            onClick={handlePreferencesSave}
            className="px-8 py-3 border-[#1CD760] border-2 text-[#1CD760] font-medium rounded-lg transition-colors duration-200 hover:bg-[#1CD760] hover:text-white"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
